import { FormGroup } from 'reactstrap';
import { useFormikContext } from 'formik';
import { Fragment, useMemo } from 'react';
import { identity } from 'lodash';
import { DropdownButtonField, ValidatedInput } from '../template-components';
import {
  asRemainderMinutes,
  asRemainderSeconds,
  asWholeHours,
  hmsToMilliseconds,
} from '../../../utils/formatters';
import { multiplePlaceholder } from '../../../utils/bulkEdits';
import { useRenderWithBulkErrors } from '../../../hooks/useRenderWithBulkErrors';
import { useUnits } from '../../../contexts/UnitContext';

const OperationDurationFormGroup = ({ operationUnitSpecMap, unitOptions }) => {
  const { errors, setFieldValue, touched, values } = useFormikContext();
  const { defaultRenderBulkError } = useRenderWithBulkErrors('operations');
  const { millisecondsPerUnit, unitForMilliseconds } = values;
  const { transformToUnit = identity } = useUnits(
    operationUnitSpecMap[unitForMilliseconds],
  );

  // Convert given milliseconds into an object with hours, minutes, and seconds
  // The resulting values are formatted in the current unit system
  const millisecondsAsHms = useMemo(
    () => (milliseconds) => {
      const transformedMilliseconds = transformToUnit(milliseconds);
      const hours = asWholeHours(transformedMilliseconds);
      const minutes = asRemainderMinutes(transformedMilliseconds);
      const seconds = asRemainderSeconds(transformedMilliseconds);
      return { hours, minutes, seconds };
    },
    [transformToUnit],
  );

  // Given the current value for hours, calculate the new millisecondsPerUnit
  // from the previous value, broken into minutes and seconds
  const hoursToMilliseconds = useMemo(
    () => (hours) => {
      const { minutes, seconds } = millisecondsAsHms(millisecondsPerUnit);
      return hmsToMilliseconds({ hours, minutes, seconds });
    },
    [millisecondsAsHms, millisecondsPerUnit],
  );

  // Given the current value for minutes, calculate the new millisecondsPerUnit
  // from the previous value, broken into hours and seconds
  const minutesToMilliseconds = useMemo(
    () => (minutes) => {
      const { hours, seconds } = millisecondsAsHms(millisecondsPerUnit);
      return hmsToMilliseconds({ hours, minutes, seconds });
    },
    [millisecondsAsHms, millisecondsPerUnit],
  );

  // Given the current value for seconds, calculate the new millisecondsPerUnit
  // from the previous value, broken into hours and minutes
  const secondsToMilliseconds = useMemo(
    () => (seconds) => {
      const { hours, minutes } = millisecondsAsHms(millisecondsPerUnit);
      return hmsToMilliseconds({ hours, minutes, seconds });
    },
    [millisecondsAsHms, millisecondsPerUnit],
  );

  const onChangeUnit = (newValue) => {
    if (newValue === 'tree') {
      setFieldValue('lossEvents', []);
      setFieldValue('revenueStreams', null);
    } else if (newValue === 'lb') {
      setFieldValue('lossEvents', null);
      setFieldValue('revenueStreams', []);
    } else {
      setFieldValue('lossEvents', null);
      setFieldValue('revenueStreams', null);
    }
  };

  const UnitDropdownWrapper =
    touched.millisecondsPerUnit && errors.millisecondsPerUnit
      ? FormGroup
      : Fragment;

  return (
    <ValidatedInput
      changeOnBlur
      format={asWholeHours}
      label="Duration"
      maxWidth="65px"
      name="millisecondsPerUnit"
      parse={hoursToMilliseconds}
      placeholder={multiplePlaceholder(millisecondsPerUnit, '00', true)}
      size="med"
      type="number"
      unit="hr"
      unitSpec={operationUnitSpecMap[unitForMilliseconds]}
    >
      {({ renderError, renderInput, renderLabel }) => (
        <FormGroup>
          {renderLabel()}
          <div className="d-flex">
            <FormGroup className="mb-0">
              {renderInput()}
              {renderError()}
            </FormGroup>
            <ValidatedInput
              changeOnBlur
              formGroupClassName="mb-0"
              format={asRemainderMinutes}
              maxWidth="65px"
              min={0}
              name="millisecondsPerUnit"
              parse={minutesToMilliseconds}
              placeholder={multiplePlaceholder(millisecondsPerUnit, '00', true)}
              size="med"
              type="number"
              unit="min"
              unitSpec={operationUnitSpecMap[unitForMilliseconds]}
            />
            <ValidatedInput
              changeOnBlur
              formGroupClassName="mb-0"
              format={asRemainderSeconds}
              maxWidth="90px"
              min={0}
              name="millisecondsPerUnit"
              parse={secondsToMilliseconds}
              placeholder={multiplePlaceholder(
                millisecondsPerUnit,
                '00.000',
                true,
              )}
              size="med"
              step={0.001}
              type="number"
              unit="sec"
              unitSpec={operationUnitSpecMap[unitForMilliseconds]}
            />
            <UnitDropdownWrapper>
              <DropdownButtonField
                inModal
                name="unitForMilliseconds"
                onChange={onChangeUnit}
                options={unitOptions}
                outline
                placeholder={multiplePlaceholder(unitForMilliseconds)}
                showOverflow
                size="med"
              />
            </UnitDropdownWrapper>
          </div>
          {defaultRenderBulkError(
            errors,
            touched,
            'millisecondsPerUnit',
            'operations',
          )}
        </FormGroup>
      )}
    </ValidatedInput>
  );
};

export default OperationDurationFormGroup;
